import { useMemo, useState } from 'react'
import { useWeeklyInsightsData } from '../hooks/useWeeklyInsightsData.js'
import { useCurrentUnit } from '../hooks/useCurrentUnit.js'
import { formatMoney } from '../lib/format.js'
import { supabase } from '../lib/supabase.js'
import { AGENT_LABELS } from '../lib/agents.js'
import { Skeleton, SkeletonCard, SkeletonList } from './Skeleton.jsx'
import MenuKillCandidatesCard from './MenuKillCandidatesCard.jsx'
import MenuEngineeringCard from './MenuEngineeringCard.jsx'
import MenuMarginLeaderboardCard from './MenuMarginLeaderboardCard.jsx'
import SalesForecastCard from './SalesForecastCard.jsx'
import KitchenLoader from './KitchenLoader.jsx'
import AskAiBar from './AskAiBar.jsx'

// Weekly Insights — last 7 days vs prior 7 days, with AI brief.
//
// Layout:
//   1. AI brief card (auto-fires once data is ready)
//   2. Summary tiles for each metric showing this-week / delta
//   3. Per-metric breakdown
//
// Manager visits this Monday morning to plan the week.
export default function WeeklyInsightsScreen() {
  const { snapshot, loading, everHadSales, refresh } = useWeeklyInsightsData()

  // Multi-unit scope for the Ask-AI box. null for single-unit / roll-up;
  // the ask-restaurant edge fn falls back to the caller's first
  // membership in that case (no single-unit regression).
  const { currentRestaurantId } = useCurrentUnit()

  const [brief, setBrief]    = useState(null)
  const [phase, setPhase]    = useState('idle') // idle | generating | ready | error
  const [error, setError]    = useState(null)

  // Lifted state for the menu engineering AI brief — generated inside
  // MenuEngineeringCard, consumed here so MenuKillCandidatesCard can
  // show per-item rationales in its drill-down modal.
  const [menuBrief, setMenuBrief] = useState(null)
  const menuBriefByItem = useMemo(() => {
    return (menuBrief?.item_rationales || []).reduce((acc, r) => {
      if (r?.item_id) acc[r.item_id] = r.rationale
      return acc
    }, {})
  }, [menuBrief])

  // Deterministic zero-activity predicate for the trailing window. True
  // only when the snapshot has loaded AND there is literally no sales,
  // labor, or waste activity across BOTH the current and prior weeks.
  // NOTE: this alone is NOT enough to show the brand-new nudge — an
  // established account can have a genuinely quiet 14-day window (stale
  // import, seasonal closure, slow fortnight). See showNewAccountNudge.
  const isZeroActivity = useMemo(() => {
    if (!snapshot) return false
    const s = snapshot.sales
    const noSales =
      (s?.this_week?.net || 0) === 0 &&
      (s?.prior_week?.net || 0) === 0 &&
      (s?.this_week?.transaction_count || 0) === 0 &&
      (s?.prior_week?.transaction_count || 0) === 0
    const noLabor =
      (snapshot.labor?.this_week?.cost || 0) === 0 &&
      (snapshot.labor?.prior_week?.cost || 0) === 0
    const noWaste =
      (snapshot.waste?.this_week?.total || 0) === 0 &&
      (snapshot.waste?.prior_week?.total || 0) === 0
    return noSales && noLabor && noWaste
  }, [snapshot])

  // The brand-new-account nudge gate. Fires ONLY for an account that has
  // NEVER recorded a sale (everHadSales === false), AND whose trailing
  // window is also empty. An established account with a zero window
  // (everHadSales true) falls through to the full tiles + brief exactly
  // as pre-ship — no regression, no hidden brief, Generate stays live.
  //
  // Fail safe: everHadSales is null while the lifetime probe is loading
  // or if it errored; the strict `=== false` check means we NEVER show
  // the nudge in those cases — we default to the normal render.
  const showNewAccountNudge = everHadSales === false && isZeroActivity

  async function generate() {
    if (phase === 'generating' || !snapshot) return
    setPhase('generating')
    setError(null)
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
      const anonKey     = import.meta.env.VITE_SUPABASE_ANON_KEY
      const { data: sess } = await supabase.auth.getSession()
      const token = sess?.session?.access_token || anonKey

      const resp = await fetch(`${supabaseUrl}/functions/v1/generate-weekly-insights`, {
        method: 'POST',
        headers: {
          'content-type':  'application/json',
          'authorization': `Bearer ${token}`,
          'apikey':        anonKey,
        },
        body: JSON.stringify({ snapshot }),
      })
      if (!resp.ok) {
        const errText = await resp.text()
        throw new Error(`HTTP ${resp.status}: ${errText.slice(0, 200) || resp.statusText}`)
      }
      const data = await resp.json()
      if (data.error) {
        // Keep the raw detail in the console for operators; show the
        // customer a calm deterministic message (no raw error / status).
        // eslint-disable-next-line no-console
        console.error('[WeeklyInsightsScreen] brief error', data)
        setError("Couldn't generate this week's brief — tap Refresh to try again.")
        setPhase('error')
        return
      }
      setBrief(data)
      setPhase('ready')
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[WeeklyInsightsScreen] generate failed', e)
      setError("Couldn't generate this week's brief — tap Refresh to try again.")
      setPhase('error')
    }
  }

  // Click-to-generate. Was auto-firing on every visit to Insights,
  // which added up. Now: idle until the manager taps Generate.
  // Same change as the daily brief in HuddleBriefCard.

  if (loading && !snapshot) {
    return (
      <div className="min-h-full bg-white pb-12">
        <main className="mx-auto max-w-screen-sm">
          <SkeletonCard />
          <section className="mx-4 mt-6">
            <Skeleton className="h-4 w-32" />
            <SkeletonList rows={4} className="mt-2" />
          </section>
        </main>
      </div>
    )
  }

  const tone = TONES[brief?.mood] || TONES.neutral

  return (
    <div className="min-h-full bg-white pb-12">
      <main className="mx-auto max-w-screen-sm">
        {/* Header */}
        <section className="mx-4 mt-4 rounded-2xl border border-black/10 bg-white p-5 shadow-sm">
          <div className="text-xs font-semibold uppercase tracking-wide text-black/55">
            Weekly insights
          </div>
          <p className="mt-1 text-xs text-black/60 leading-snug">
            Last 7 days vs. the 7 days before. AI scans sales, labor, waste,
            receivings, and prime cost for trends worth knowing.
          </p>
          {snapshot?.windows && (
            <div className="mt-2 text-xs text-black/55 tabular-nums">
              {snapshot.windows.this_week_start} → {snapshot.windows.this_week_end} {' '}
              vs {' '}
              {snapshot.windows.prior_week_start} → {snapshot.windows.prior_week_end}
            </div>
          )}
        </section>

        {/* Brand-new-account nudge — deterministic, no AI call. Replaces
            the wall of $0.00 / 0 / —% tiles on an account that has NEVER
            recorded a sale with a clear next action. Gated on
            showNewAccountNudge (everHadSales === false AND zero window);
            an established account — even one with a quiet 14-day window —
            skips this and sees the full brief + tiles. */}
        {showNewAccountNudge && (
          <section className="mx-4 mt-4 rounded-2xl border border-black/10 bg-white p-5 shadow-sm">
            <div className="text-base font-semibold text-black/90">
              No sales yet this week
            </div>
            <p className="mt-1 text-sm text-black/65 leading-snug">
              Connect your POS or import sales to see your weekly insights.
              Once a few days of sales, labor, or waste land, this screen
              fills in with week-over-week trends and a brief.
            </p>
          </section>
        )}

        {/* AI brief */}
        {!showNewAccountNudge && (
        <section
          className={`mx-4 mt-4 rounded-2xl border p-5 shadow-sm ${tone.border} ${tone.bg}`}
          aria-label={AGENT_LABELS.weeklyInsights}
        >
          <div className="flex items-baseline justify-between gap-2">
            <div className={`text-[11px] font-semibold uppercase tracking-wide ${tone.label}`}>
              {AGENT_LABELS.weeklyInsights}
            </div>
            {phase !== 'idle' && (
              <button
                type="button"
                onClick={generate}
                disabled={phase === 'generating' || !snapshot}
                className="rounded-md px-2 py-1 text-xs font-semibold text-blue-700 hover:bg-blue-50 disabled:opacity-50"
              >
                {phase === 'generating' ? '…' : 'Refresh'}
              </button>
            )}
          </div>

          {phase === 'idle' && (
            <div className="mt-2">
              <p className="text-sm text-black/75 leading-snug">
                Tap below for the week's brief — wins, concerns, and what to focus on next week. Numbers above are live; the AI just narrates them.
              </p>
              <button
                type="button"
                onClick={generate}
                disabled={!snapshot}
                className="mt-3 w-full rounded-lg bg-blue-600 px-3 py-2 text-sm font-bold text-white shadow-sm hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                ✨ Generate this week's brief
              </button>
              <div className="mt-1.5 text-[11px] text-black/50">
                Run it once a week — Monday morning works well.
              </div>
            </div>
          )}

          {phase === 'generating' && (
            <div className="mt-2">
              <KitchenLoader
                align="row"
                size="sm"
                messages={[
                  'Pulling the week\u0027s numbers\u2026',
                  'Comparing to last week\u2026',
                  'Plating your insights\u2026',
                ]}
              />
            </div>
          )}

          {phase === 'error' && (
            <div className="mt-2 text-sm text-red-700">{error}</div>
          )}

          {phase === 'ready' && brief && (
            <>
              <div className={`mt-2 text-base font-semibold leading-snug ${tone.headline}`}>
                {brief.headline}
              </div>

              {brief.top_wins?.length > 0 && (
                <div className="mt-3">
                  <div className={`text-[11px] font-semibold uppercase tracking-wide ${tone.label}`}>
                    Wins
                  </div>
                  <ul className="mt-1 space-y-1.5 text-sm leading-snug">
                    {brief.top_wins.map((b, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <span className="mt-1.5 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-500" />
                        <span className="text-black/85">{b}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {brief.top_concerns?.length > 0 && (
                <div className="mt-3">
                  <div className={`text-[11px] font-semibold uppercase tracking-wide ${tone.label}`}>
                    Concerns
                  </div>
                  <ul className="mt-1 space-y-1.5 text-sm leading-snug">
                    {brief.top_concerns.map((b, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <span className="mt-1.5 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-red-500" />
                        <span className="text-black/85">{b}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {brief.focus_for_next_week?.length > 0 && (
                <div className="mt-3 rounded-md bg-white/70 px-3 py-2 text-sm">
                  <div className={`text-[11px] font-semibold uppercase tracking-wide ${tone.label}`}>
                    Focus for next week
                  </div>
                  <ul className="mt-1 list-disc space-y-1 pl-5">
                    {brief.focus_for_next_week.map((b, i) => (
                      <li key={i} className="text-black/85 leading-snug">{b}</li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
        </section>
        )}

        {/* Summary tiles */}
        {!showNewAccountNudge && snapshot && (
          <section className="mx-4 mt-4 grid grid-cols-2 gap-3" aria-label="Week-over-week metrics">
            <MetricTile
              label="Sales (net)"
              thisVal={formatMoney(snapshot.sales.this_week.net)}
              priorVal={formatMoney(snapshot.sales.prior_week.net)}
              delta={snapshot.sales.delta_net}
              deltaIsCurrency
              betterDir="up"
            />
            <MetricTile
              label="Transactions"
              thisVal={snapshot.sales.this_week.transaction_count}
              priorVal={snapshot.sales.prior_week.transaction_count}
              delta={snapshot.sales.delta_tx}
              betterDir="up"
            />
            <MetricTile
              label="Labor cost"
              thisVal={formatMoney(snapshot.labor.this_week.cost)}
              priorVal={formatMoney(snapshot.labor.prior_week.cost)}
              delta={snapshot.labor.delta_cost}
              deltaIsCurrency
              betterDir="down"
              hint={
                snapshot.labor.labor_pct_of_sales_this != null
                  ? `${snapshot.labor.labor_pct_of_sales_this}% of sales`
                  : null
              }
            />
            <MetricTile
              label="Waste cost"
              thisVal={formatMoney(snapshot.waste.this_week.total)}
              priorVal={formatMoney(snapshot.waste.prior_week.total)}
              delta={snapshot.waste.delta_cost}
              deltaIsCurrency
              betterDir="down"
              hint={
                snapshot.waste.waste_pct_of_sales_this != null
                  ? `${snapshot.waste.waste_pct_of_sales_this}% of sales`
                  : null
              }
            />
            <MetricTile
              label="Receivings"
              thisVal={formatMoney(snapshot.receivings.this_week.total)}
              priorVal={formatMoney(snapshot.receivings.prior_week.total)}
              delta={snapshot.receivings.delta_cost}
              deltaIsCurrency
              betterDir="neutral"
            />
            <MetricTile
              label="Prime cost"
              thisVal={
                snapshot.prime_cost.this_week_pct != null
                  ? `${snapshot.prime_cost.this_week_pct}%`
                  : '—'
              }
              priorVal={
                snapshot.prime_cost.prior_week_pct != null
                  ? `${snapshot.prime_cost.prior_week_pct}%`
                  : '—'
              }
              delta={
                snapshot.prime_cost.this_week_pct != null && snapshot.prime_cost.prior_week_pct != null
                  ? Number((snapshot.prime_cost.this_week_pct - snapshot.prime_cost.prior_week_pct).toFixed(2))
                  : null
              }
              deltaSuffix="pp"
              betterDir="down"
              hint="industry healthy 55–60%"
            />
          </section>
        )}

        {/* Top wasted items breakdown */}
        {snapshot?.waste?.this_week?.top_items?.length > 0 && (
          <section className="mx-4 mt-6">
            <div className="mb-2 text-base font-semibold">
              Top wasted items this week
            </div>
            <ul className="overflow-hidden rounded-xl border border-black/10">
              {snapshot.waste.this_week.top_items.map((it, i) => (
                <li
                  key={i}
                  className="flex items-baseline justify-between gap-3 border-b border-black/5 px-4 py-3 text-sm last:border-b-0"
                >
                  <div className="min-w-0 flex-1 truncate font-medium">{it.item_name}</div>
                  <div className="shrink-0 font-semibold tabular-nums text-red-700">
                    {formatMoney(it.cost)}
                  </div>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* 7-day revenue forecast — day-of-week seasonal model from
            the last 12 weeks. Sits before menu kill so the manager
            sees the forward look first, then the prune list. */}
        <SalesForecastCard />

        {/* Menu engineering matrix — Stars / Plowhorses / Puzzles / Dogs
            from vw_menu_engineering. Strategic view of the whole menu
            sits BEFORE the menu-kill prune list, since the matrix is
            the broader analysis and kill candidates are a subset of
            the dogs quadrant. */}
        <MenuEngineeringCard onBriefGenerated={setMenuBrief} />

        {/* Top 5 by 90-day contribution margin — "protect these items"
            companion to the matrix. */}
        <MenuMarginLeaderboardCard />

        {/* Menu kill candidates — items that barely sold over the
            last 90 days. Heuristic + tenant scoping live in
            vw_menu_kill_candidates; this card just renders. */}
        <MenuKillCandidatesCard briefByItem={menuBriefByItem} />

        {/* Ask your restaurant — real NL query box (Ship I). Insights tab
            only: mode='insights-query' makes the modal call the
            ask-restaurant edge fn (menu / margin / compliance questions).
            Every other tab keeps the coming-soon stub. */}
        <AskAiBar
          placeholder="Ask about your menu, margins, or compliance…"
          mode="insights-query"
          restaurantId={currentRestaurantId}
        />
      </main>
    </div>
  )
}

function MetricTile({ label, thisVal, priorVal, delta, deltaIsCurrency, deltaSuffix, betterDir, hint }) {
  // Color the delta:
  //   - "up": positive delta = good (sales, transactions)
  //   - "down": positive delta = bad (labor, waste, prime cost)
  //   - "neutral": informational only
  const numDelta = Number(delta || 0)
  let deltaTone = 'text-black/55'
  if (delta != null && betterDir === 'up') {
    deltaTone = numDelta > 0 ? 'text-emerald-700' : numDelta < 0 ? 'text-red-700' : 'text-black/55'
  } else if (delta != null && betterDir === 'down') {
    deltaTone = numDelta > 0 ? 'text-red-700' : numDelta < 0 ? 'text-emerald-700' : 'text-black/55'
  }
  const sign = delta == null ? '' : (numDelta > 0 ? '+' : '')
  const formatted = delta == null
    ? '—'
    : deltaIsCurrency
      ? `${sign}${formatMoney(numDelta)}`
      : deltaSuffix
        ? `${sign}${numDelta}${deltaSuffix}`
        : `${sign}${numDelta}`

  return (
    <div className="rounded-xl border border-black/10 bg-white p-3">
      <div className="text-[10px] font-semibold uppercase tracking-wide text-black/55">
        {label}
      </div>
      <div className="mt-0.5 text-lg font-bold tabular-nums text-black/90">
        {thisVal}
      </div>
      <div className="mt-0.5 text-xs text-black/55 tabular-nums">
        prior: {priorVal}
      </div>
      <div className={`mt-0.5 text-xs font-semibold tabular-nums ${deltaTone}`}>
        {formatted}
      </div>
      {hint && (
        <div className="mt-1 text-[10px] text-black/45 leading-tight">{hint}</div>
      )}
    </div>
  )
}

const TONES = {
  positive:   { border: 'border-emerald-200', bg: 'bg-emerald-50/60', label: 'text-emerald-900/70', headline: 'text-emerald-900' },
  neutral:    { border: 'border-blue-200',    bg: 'bg-blue-50/40',    label: 'text-blue-900/70',    headline: 'text-blue-900' },
  concerning: { border: 'border-red-200',     bg: 'bg-red-50/40',     label: 'text-red-900/70',     headline: 'text-red-900' },
}
